import * as React from 'react';

type TopbarMode = 'default' | 'portal';

type TopbarPortalContextValue = {
  enabled: boolean;
  mode: TopbarMode;
  portalNode: HTMLDivElement | null;
  setPortalNode: (node: HTMLDivElement | null) => void;
  subscribePortalConsumer: (_onStoreChange: () => void) => () => void;
};

const EMPTY_PORTAL_CONSUMER_SNAPSHOT = 0;
const NOOP_UNSUBSCRIBE = () => undefined;
const NOOP_SUBSCRIBE_PORTAL_CONSUMER = () => NOOP_UNSUBSCRIBE;

const TopbarPortalContext = React.createContext<TopbarPortalContextValue>({
  enabled: false,
  mode: 'default',
  portalNode: null,
  setPortalNode: () => undefined,
  subscribePortalConsumer: NOOP_SUBSCRIBE_PORTAL_CONSUMER,
});

export function TopbarPortalProvider({ children }: { children: React.ReactNode }) {
  const [portalConsumers, setPortalConsumers] = React.useState(0);
  const [portalNode, setPortalNode] = React.useState<HTMLDivElement | null>(null);
  const mode: TopbarMode = portalConsumers > 0 ? 'portal' : 'default';

  const subscribePortalConsumer = React.useCallback((_onStoreChange: () => void) => {
    setPortalConsumers((current) => current + 1);
    return () => {
      setPortalConsumers((current) => Math.max(0, current - 1));
    };
  }, []);

  const value = React.useMemo(
    () => ({
      enabled: true,
      mode,
      portalNode,
      setPortalNode,
      subscribePortalConsumer,
    }),
    [mode, portalNode, subscribePortalConsumer],
  );

  return <TopbarPortalContext.Provider value={value}>{children}</TopbarPortalContext.Provider>;
}

export function useTopbarPortal() {
  return React.useContext(TopbarPortalContext);
}

export function useTopbarPortalSlot() {
  const { enabled, portalNode, subscribePortalConsumer } = useTopbarPortal();

  React.useSyncExternalStore(
    enabled ? subscribePortalConsumer : NOOP_SUBSCRIBE_PORTAL_CONSUMER,
    () => EMPTY_PORTAL_CONSUMER_SNAPSHOT,
    () => EMPTY_PORTAL_CONSUMER_SNAPSHOT,
  );

  return portalNode;
}
